import { FormInput } from "@general-backoffice/core";
import type { TaxProfile } from "@investhinks/api-client";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Row, Spinner } from "reactstrap";

import useCountryRegionFormOptions from "../../../../hooks/useCountryRegionFormOptions";
import { buildTaxIdTypeOptions, getAllowedTaxIdTypes, getTaxIdPlaceholder } from "../taxProfileConstants";
import { detectSpanishTaxIdType, isEUCountry, isNonEUCountry, validateEUVAT } from "../validators";

type TaxProfileData = Pick<TaxProfile, "profileType" | "taxIdType" | "taxIdNumber" | "name" | "fiscalAddress" | "country" | "viesVerified" | "viesName" | "viesAddress"> & Record<string, unknown>;

interface IdentificationStepProps {
  taxProfile: TaxProfileData | null;
  handleInputChange: (name: string, value: unknown) => void;
  handleInputEvent: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVerifyVIES: (vatNumber: string) => Promise<void>;
  isLoading: boolean;
  validationError?: string;
  t: (key: string, fallback?: string) => string;
}

const IdentificationStep = ({
  taxProfile,
  handleInputChange,
  handleInputEvent,
  onVerifyVIES,
  isLoading,
  validationError,
  t,
}: IdentificationStepProps) => {
  const [isVerifying, setIsVerifying] = useState(false);

  const isEUBusiness = taxProfile?.profileType === "EU_BUSINESS";

  // Country selector for NON_EU types and EU_PRIVATE
  const requiresCountrySelection = ["NON_EU_BUSINESS", "NON_EU_PRIVATE", "EU_PRIVATE"].includes(
    taxProfile?.profileType || ""
  );

  const isEUType = ["EU_PRIVATE", "EU_BUSINESS"].includes(taxProfile?.profileType || "");
  const isNonEUType = ["NON_EU_BUSINESS", "NON_EU_PRIVATE"].includes(taxProfile?.profileType || "");

  const { countryOptions: allCountryOptions } = useCountryRegionFormOptions({
    country: taxProfile?.country ?? undefined,
  });

  // Filter country options based on profile type
  // EU types: only EU countries (excluding Spain)
  // NON_EU types: only non-EU countries
  const countryOptions = useMemo(() => {
    if (!allCountryOptions) return [];
    if (isEUType) {
      return allCountryOptions.filter((o) => isEUCountry(o.value));
    }
    if (isNonEUType) {
      return allCountryOptions.filter((o) => isNonEUCountry(o.value));
    }
    return allCountryOptions;
  }, [allCountryOptions, isEUType, isNonEUType]);

  const selectedCountry = countryOptions?.find((o) => o.value === taxProfile?.country);

  // Clear country if it's incompatible with the profile type
  useEffect(() => {
    if (!taxProfile?.country || !taxProfile?.profileType) return;

    const country = taxProfile.country;

    // Check if country is valid for the profile type
    if (isEUType && !isEUCountry(country)) {
      handleInputChange("country", null);
    } else if (isNonEUType && !isNonEUCountry(country)) {
      handleInputChange("country", null);
    }
  }, [taxProfile?.profileType, taxProfile?.country, isEUType, isNonEUType, handleInputChange]);

  // Filter tax ID types based on selected referrer type
  const allowedTypes = getAllowedTaxIdTypes(taxProfile?.profileType || "");
  const showTaxIdTypeSelector = allowedTypes.length > 1;

  const taxIdTypeOptions = useMemo(
    () => buildTaxIdTypeOptions(taxProfile?.profileType || "", t),
    [taxProfile?.profileType, t]
  );

  const selectedType = taxIdTypeOptions.find((o) => o.value === taxProfile?.taxIdType);

  // For EU Business, check VAT number format
  const vatNumber = taxProfile?.taxIdNumber || "";
  const hasValidCountryCode = vatNumber ? validateEUVAT(vatNumber).valid : false;

  // Auto-select tax ID type when only one option is valid
  useEffect(() => {
    if (!taxProfile?.profileType) return;

    // If only one type is allowed, auto-select it
    if (allowedTypes.length === 1) {
      if (taxProfile?.taxIdType !== allowedTypes[0]) {
        handleInputChange("taxIdType", allowedTypes[0]);
      }
    }
    // If current type is not in allowed list, reset it
    else if (taxProfile?.taxIdType && !allowedTypes.includes(taxProfile.taxIdType)) {
      handleInputChange("taxIdType", allowedTypes[0] || null);
    }
  }, [taxProfile?.profileType, allowedTypes, taxProfile?.taxIdType, handleInputChange]);

  // Auto-capitalize prefilled tax ID
  useEffect(() => {
    const value = taxProfile?.taxIdNumber;
    if (value && value !== value.toUpperCase()) {
      handleInputChange("taxIdNumber", value.toUpperCase());
    }
  }, [taxProfile?.taxIdNumber, handleInputChange]);

  // Auto-detect Spanish tax ID type (NIF, NIE, CIF) when user enters a valid ID
  useEffect(() => {
    if (!taxProfile?.profileType?.startsWith("SPANISH_")) return;
    if (allowedTypes.length <= 1) return;

    const detected = detectSpanishTaxIdType(taxProfile?.taxIdNumber);
    if (detected && allowedTypes.includes(detected) && detected !== taxProfile?.taxIdType) {
      handleInputChange("taxIdType", detected);
    }
  }, [taxProfile?.taxIdNumber, taxProfile?.profileType, allowedTypes, taxProfile?.taxIdType, handleInputChange]);

  const handleVerifyVIES = async () => {
    setIsVerifying(true);
    try {
      await onVerifyVIES(taxProfile?.taxIdNumber || "");
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle VAT number change - auto-capitalize and clear verification if number changes
  const handleVatNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    const oldValue = taxProfile?.taxIdNumber || "";

    // If number changed and was previously verified, clear verification
    if (newValue !== oldValue && taxProfile?.viesVerified) {
      handleInputChange("viesVerified", false);
      handleInputChange("viesVerifiedAt", null);
      handleInputChange("viesName", null);
      handleInputChange("viesAddress", null);
    }

    handleInputChange("taxIdNumber", newValue);
  };

  // Handle tax ID change - auto-capitalize
  const handleTaxIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange("taxIdNumber", e.target.value.toUpperCase());
  };

  // For EU Business, show VAT-specific UI
  if (isEUBusiness) {
    return (
      <div>
        <h5 className="mb-3">{t("steps.identification.title", "Identification")}</h5>
        <p className="text-muted mb-4">
          {t("steps.identification.euVatDescription", "Enter your EU VAT number including the country code prefix.")}
        </p>

        <FormInput.Input
          type="text"
          onChange={handleVatNumberChange}
          value={taxProfile?.taxIdNumber || ""}
          name="taxIdNumber"
          label={t("steps.vies.vatNumber", "VAT Number") + " *"}
          disabled={isLoading}
          placeholder="BG207883265"
        />

        {/* Warning if VAT number doesn't start with EU country code */}
        {vatNumber && !hasValidCountryCode && !taxProfile?.viesVerified && (
          <Alert color="warning" className="mt-3">
            <h5 className="alert-heading">
              <i className="fas fa-exclamation-triangle mr-2" />
              {t("steps.vies.invalidFormat", "Invalid VAT Number Format")}
            </h5>
            <p className="mb-2">
              {t(
                "steps.vies.countryCodeRequired",
                "Your VAT number must start with a 2-letter EU country code (e.g., DE for Germany, FR for France, BG for Bulgaria)."
              )}
            </p>
            <hr />
            <p className="mb-0">
              <strong>{t("steps.vies.whatIsVIES", "What is VIES?")}</strong>{" "}
              {t(
                "steps.vies.viesExplanation",
                "VIES (VAT Information Exchange System) is the European Commission's database for validating EU VAT numbers. To receive tax-exempt payments as an EU business, your company must be registered for intra-community VAT and have a valid VAT number that can be verified through VIES."
              )}
            </p>
          </Alert>
        )}

        {/* VIES Verification Status */}
        {taxProfile?.viesVerified ? (
          <Alert color="success" className="mt-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-check-circle fa-2x mr-3" />
              <div>
                <strong>{t("steps.vies.verified", "Verified")}</strong>
                {taxProfile?.viesName && <div className="mt-1">{taxProfile.viesName}</div>}
              </div>
            </div>
          </Alert>
        ) : (
          vatNumber &&
          hasValidCountryCode && (
            <Button
              color="primary"
              onClick={handleVerifyVIES}
              disabled={isLoading || isVerifying}
              block
              className="mt-3"
            >
              {isVerifying ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {t("steps.vies.verifying", "Verifying...")}
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle mr-2" />
                  {t("steps.vies.verifyButton", "Verify VAT Number")}
                </>
              )}
            </Button>
          )
        )}
      </div>
    );
  }

  // Check if this is an individual type (requires name field)
  const isIndividualType = ["SPANISH_PARTICULAR", "EU_PRIVATE", "NON_EU_PRIVATE"].includes(
    taxProfile?.profileType || ""
  );

  // Standard identification for non-EU-Business types
  return (
    <div>
      <h5 className="mb-3">{t("steps.identification.title", "Identification")}</h5>
      <p className="text-muted mb-4">
        {t("steps.identification.description", "Please provide your tax identification and address.")}
      </p>

      {/* Name field for individual types */}
      {isIndividualType && (
        <FormInput.Input
          type="text"
          onChange={handleInputEvent}
          value={taxProfile?.name || ""}
          name="name"
          label={t("steps.identification.nameLabel", "Full Name") + " *"}
          disabled={isLoading}
          placeholder={t("steps.identification.namePlaceholder", "Enter your full legal name")}
          className="mb-3"
        />
      )}

      <Row>
        {showTaxIdTypeSelector ? (
          <>
            <Col md={4}>
              <FormInput.Select
                label={t("steps.identification.typeLabel", "ID Type")}
                name="taxIdType"
                value={selectedType}
                options={taxIdTypeOptions}
                onChange={({ value }) => handleInputChange("taxIdType", value)}
                isDisabled={isLoading}
              />
            </Col>
            <Col md={8}>
              <FormInput.Input
                type="text"
                onChange={handleTaxIdChange}
                value={taxProfile?.taxIdNumber || ""}
                name="taxIdNumber"
                label={t("steps.identification.numberLabel", "Tax ID Number")}
                disabled={isLoading}
                placeholder={getTaxIdPlaceholder(taxProfile?.taxIdType)}
              />
            </Col>
          </>
        ) : (
          <Col md={12}>
            <FormInput.Input
              type="text"
              onChange={handleTaxIdChange}
              value={taxProfile?.taxIdNumber || ""}
              name="taxIdNumber"
              label={`${t(`taxIdTypes.${taxProfile?.taxIdType}`, taxProfile?.taxIdType || t("steps.identification.numberLabel", "Tax ID Number"))} *`}
              disabled={isLoading}
              placeholder={getTaxIdPlaceholder(taxProfile?.taxIdType)}
            />
          </Col>
        )}
      </Row>

      {validationError && taxProfile?.taxIdNumber && (
        <Alert color="danger" className="mt-3">
          <i className="fas fa-exclamation-circle mr-2" />
          {validationError}
        </Alert>
      )}

      <FormInput.Input
        type="textarea"
        rows={2}
        onChange={handleInputEvent}
        value={taxProfile?.fiscalAddress || ""}
        name="fiscalAddress"
        label={t("steps.identification.addressLabel", "Fiscal Address")}
        disabled={isLoading}
        className="mt-3"
      />

      {/* Country selector for NON_EU types and EU_PRIVATE */}
      {requiresCountrySelection && (
        <FormInput.Select
          label={t("steps.identification.countryLabel", "Country") + " *"}
          name="country"
          value={selectedCountry}
          options={countryOptions}
          onChange={({ value }) => handleInputChange("country", value)}
          isDisabled={isLoading}
          isSearchable={true}
          placeholder={t("steps.identification.countryPlaceholder", "Select your country...")}
          className="mt-3"
        />
      )}
    </div>
  );
};

export default IdentificationStep;
